import YearFields from '../components/YearFields';
import { datingDraft, datingPayload } from '../utils/dating';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import SearchableSelect from '../components/SearchableSelect';
import RichTextEditor from '../components/RichTextEditor';
import PhotoUploader, { uploadButtonLabel, usePhotoUploads } from '../components/PhotoUploader';
import ThumbnailImage from '../components/ThumbnailImage';

export default function EditProductPage() {
  const [dating, setDating] = useState(() => datingDraft());
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [manufacturers, setManufacturers] = useState([]);
  const uploads = usePhotoUploads();
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  const uploadHost = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';

  const BELONGS_TO_OPTIONS = [
    { id: 'максим', name: 'Максим' },
    { id: 'станок', name: 'Станок' },
  ];

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const [pRes, mRes, listRes] = await Promise.all([
          api.get(`/products/${id}`),
          api.get('/manufacturers'),
          api.get('/products'),
        ]);

        if (!mounted) return;

        const p = pRes.data;
        setDating(datingDraft(p));
        setProduct({
          id: p.id,
          name: p.name || '',
          description: p.description || '',
          manufacturer: p.manufacturer || null,
          source: p.source || '',
          number: p.number || '',
          year: p.year || '',
          belongsTo: p.belongsTo || '',
          parent: p.parent || null,
          photos: p.photos || []
        });

        setManufacturers(mRes.data || []);
        // исключаем текущее изделие, чтобы не выбрать самого себя родителем
        const filtered = (listRes.data || []).filter(it => it.id !== parseInt(id, 10));
        setAllProducts(filtered);
      } catch (e) {
        console.error(e);
      }
    };

    load();
    return () => { mounted = false; };
  }, [id]);

  const handleChange = (e) => {
    setProduct(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleRemovePhoto = (photoId) => {
    setProduct(prev => ({
      ...prev,
      photos: prev.photos.filter(p => p.id !== photoId)
    }));
  };

  const handleSearchPhotos = async () => {
    if (searchTerm.trim().length === 0) return;
    const res = await api.get(`/photos/search?q=${encodeURIComponent(searchTerm)}`);
    setSearchResults(res.data);
  };

  const handleAttachPhoto = (photo) => {
    setProduct(prev => {
      if (prev.photos.some(p => p.id === photo.id)) return prev;
      return { ...prev, photos: [...prev.photos, photo] };
    });
  };

  // утилита: приводим значение из SearchableSelect к числу id или null
  const toIdOrNull = (val) => {
    if (val == null || val === '') return null;
    if (typeof val === 'object') return val.id ?? null;
    if (typeof val === 'string') {
      const n = parseInt(val, 10);
      return Number.isNaN(n) ? null : n;
    }
    if (typeof val === 'number') return val;
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploads.busy || saving) return;
    if (uploads.failedCount && !window.confirm('Часть фото не загрузилась. Сохранить без них?')) return;

    setSaving(true);
    try {
      datingPayload(dating);
      const allPhotos = [
        ...(product.photos || []),
        ...uploads.photos.filter(photo => !(product.photos || []).some(p => p.id === photo.id))
      ].map(p => ({ id: p.id }));

      await api.put(`/products/${id}`, {
        ...product,
        ...datingPayload(dating),
        manufacturer: product.manufacturer?.id ? { id: product.manufacturer.id } : null,
        parent: product.parent?.id ? { id: product.parent.id } : null,
        belongsTo: product.belongsTo || null,
        photos: allPhotos
      });

      navigate(`/products/${id}`);
    } catch (error) {
      console.error('Ошибка при сохранении:', error);
      alert(error.message || 'Не удалось сохранить изменения');
      setSaving(false);
    }
  };

  if (!product) return <div className="text-white p-6">Загрузка...</div>;

  return (
    <form onSubmit={handleSubmit} className="min-h-screen bg-gray-900 text-white">
      <div className="p-6 max-w-2xl mx-auto">
        <h1 className="text-2xl mb-4">Редактировать изделие</h1>

        <label className="block mb-2">Название</label>
        <input
          name="name"
          value={product.name}
          onChange={handleChange}
          className="w-full mb-4 p-2 bg-gray-800 rounded border border-gray-700"
          placeholder="Название изделия"
        />

        <div className="mb-4">
          <label className="block mb-2">Описание</label>
          <RichTextEditor
            value={product.description || ''}
            onChange={(val) => setProduct(prev => ({ ...prev, description: val }))}
            placeholder="Введите описание"
          />
        </div>

        <label className="block mb-2">Источник</label>
        <input
          name="source"
          value={product.source}
          onChange={handleChange}
          className="w-full mb-4 p-2 bg-gray-800 rounded border border-gray-700"
          placeholder="Источник (ссылка/описание)"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>

            <YearFields value={dating} onChange={setDating} />
          </div>

          <div>
            <label className="block mb-2">Номер</label>
            <input
              name="number"
              value={product.number}
              onChange={handleChange}
              className="w-full p-2 bg-gray-800 rounded border border-gray-700"
              placeholder="№ изделия"
            />
          </div>
        </div>

        {/* BelongsTo */}
        <div className="mt-4">
          <label className="block mb-2">Назначение (belongsTo)</label>
          <SearchableSelect
            options={BELONGS_TO_OPTIONS}
            value={product.belongsTo || ''}
            onChange={(val) => {
              setProduct(prev => ({ ...prev, belongsTo: val || '' }));
            }}
            placeholder="— Выбери назначение —"
          />
        </div>

        {/* Manufacturer */}
        <div className="mt-4">
          <label className="block mb-2">Производитель</label>
          <SearchableSelect
            options={manufacturers}
            value={product.manufacturer?.id || null}
            onChange={(val) => {
              const idVal = toIdOrNull(val);
              setProduct(prev => ({
                ...prev,
                manufacturer: idVal ? { id: idVal } : null
              }));
            }}
            placeholder="— Производитель —"
          />
        </div>

        {/* Parent product */}
        <div className="mt-4">
          <label className="block mb-2">Родительское изделие</label>
          <SearchableSelect
            // важно: НЕ добавляем "— Без родителя —" в options
            options={allProducts}
            // пустое значение — именно null, чтобы плейсхолдер не становился значением
            value={product.parent?.id ?? null}
            onChange={(val) => {
              const idVal = toIdOrNull(val);
              setProduct(prev => ({
                ...prev,
                parent: idVal ? { id: idVal } : null
              }));
            }}
            placeholder="— Без родителя —"
          />
        </div>

        {/* Photos */}
        <div className="mt-6">
          <div className="mb-6">
            <PhotoUploader uploads={uploads} existing={product.photos || []} onRemoveExisting={handleRemovePhoto} />
          </div>

          <div className="mb-6">
            <label className="block mb-2">Поиск существующих фото:</label>
            <div className="flex mb-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-grow p-2 bg-gray-800 rounded mr-2 border border-gray-700"
                placeholder="Поиск по URL или другим данным"
              />
              <button
                type="button"
                onClick={handleSearchPhotos}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Найти
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {searchResults.map(photo => (
                <div key={photo.id} className="relative">
                  <ThumbnailImage
                    src={photo.url?.startsWith('/') ? `${uploadHost}${photo.url}` : photo.url}
                    className="w-24 h-24 object-cover rounded border border-gray-700"
                  />
                  <button
                    type="button"
                    onClick={() => handleAttachPhoto(photo)}
                    className="absolute bottom-0 right-0 bg-green-600 text-white rounded-full w-6 h-6 text-xs flex items-center justify-center"
                    title="Прикрепить фото"
                  >+</button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Danger + Save */}
        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={async () => {
              if (window.confirm('Удалить это изделие?')) {
                try {
                  await api.delete(`/products/${id}`);
                  navigate(-2);
                } catch (error) {
                  console.error('Ошибка при удалении:', error);
                  alert('Не удалось удалить изделие');
                }
              }
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            Удалить
          </button>
          <button
            type="submit"
            disabled={uploads.busy || saving}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded disabled:opacity-50 disabled:cursor-wait"
          >
            {saving ? 'Сохраняем…' : uploadButtonLabel(uploads, 'Сохранить')}
          </button>
        </div>
      </div>
    </form>
  );
}
